import {
  BarChart3,
  ClipboardCheck,
  FileText,
  Globe,
  GraduationCap,
  Home,
  List,
  MessageSquareWarning,
  PlusCircle,
  Settings,
  Upload,
  Users,
} from "lucide-react";
import { ADMIN_MENU_ITEMS } from "./admin/adminMenu";

/**
 * Single source of truth for the application navigation.
 * Used by both the top bar (desktop dropdowns + mobile drawer) and the
 * sidebar so the two never drift apart.
 *
 * Section: { key, title, icon, path? , items?: [{ title, path, icon, description }] }
 */
export function getNavigation(role) {
  const isAdminOrDG = role === "Admin" || role === "DG";
  const isScholarshipOwner = role === "SCHOLARSHIP_OWNER";
  const isHliAdmin = role === "HLI_ADMIN";
  const isStaff = isAdminOrDG || isScholarshipOwner || isHliAdmin;

  const sections = [];

  if (!isStaff) {
    sections.push({ key: "student", title: "Student Portal", path: "/studentPortal", icon: Home });
  }

  if (isAdminOrDG) {
    sections.push(
      { key: "dashboard", title: "Dashboard", path: "/dashboard", icon: Home },
      {
        key: "abroad",
        title: "Abroad Scholarship",
        icon: Globe,
        items: [
          { title: "Create Scholarship", path: "/addScholarship", icon: PlusCircle, description: "Add new scholarship opportunity" },
          { title: "Manage Programs", path: "/scholarshipList", icon: List, description: "View and edit programs" },
          { title: "View Applicants", path: "/applicantsList", icon: Users, description: "Review applications" },
        ],
      },
      {
        key: "local",
        title: "Local Scholarship",
        icon: GraduationCap,
        items: [
          { title: "Bursary Applications", path: "/busaryapplication", icon: FileText, description: "Manage bursary requests" },
          { title: "Loan Approval", path: "/loanApprovalStep", icon: ClipboardCheck, description: "Review loan applications" },
          { title: "Selection Settings", path: "/selection", icon: Settings, description: "Configure selection criteria" },
          { title: "Upload Students", path: "/uploadBursaryApplication", icon: Upload, description: "Bulk import student data" },
          { title: "Granted Students", path: "/GrantedStudents", icon: Users, description: "View granted students" },
          { title: "Student Claims", path: "/studentClaims", icon: MessageSquareWarning, description: "Review rejection claims" },
        ],
      },
      {
        key: "reports",
        title: "Reports",
        icon: BarChart3,
        items: [
          { title: "Evaluator Report", path: "/evaluator-report", icon: Users, description: "View evaluator statistics" },
        ],
      },
      {
        key: "admin",
        title: "Administration",
        icon: Settings,
        items: ADMIN_MENU_ITEMS.map(({ title, path, Icon, description }) => ({ title, path, icon: Icon, description })),
      }
    );
  }

  if (isScholarshipOwner) {
    sections.push({
      key: "abroad",
      title: "Abroad Scholarship",
      icon: Globe,
      items: [{ title: "View Applicants", path: "/applicantsList", icon: Users, description: "Review applications" }],
    });
  }

  if (isHliAdmin) {
    sections.push({
      key: "hli",
      title: "Local Scholarship",
      icon: GraduationCap,
      items: [
        { title: "Bursary Applications", path: "/hli/bursaryApplications", icon: FileText, description: "View institution bursary requests" },
        { title: "Loan Approval", path: "/hliAdmin", icon: ClipboardCheck, description: "Review & approve institution loans" },
        { title: "Selection Settings", path: "/hli/selection", icon: Settings, description: "Run selection for your institution" },
        { title: "Upload Students", path: "/hli/uploadStudents", icon: Upload, description: "Import admitted students" },
        { title: "Granted Students", path: "/hli/grantedStudents", icon: Users, description: "View granted students" },
        { title: "Student Claims", path: "/hli/studentClaims", icon: MessageSquareWarning, description: "Review rejection claims" },
      ],
    });
  }

  return sections;
}

/** Landing page for the current role (used by the brand link and the user menu). */
export function getHomePath(role) {
  const first = getNavigation(role)[0];
  if (!first) return "/";
  return first.path || first.items?.[0]?.path || "/";
}

/** Human readable role label. */
export function formatRole(role) {
  if (!role) return "";
  const map = {
    Admin: "Administrator",
    DG: "Director General",
    HLI_ADMIN: "Institution Admin",
    SCHOLARSHIP_OWNER: "Scholarship Owner",
    User: "Student",
  };
  return map[role] || role.replace(/_/g, " ");
}

export function getInitials(info) {
  const first = info?.firstName?.trim?.() || "";
  const last = info?.lastName?.trim?.() || "";
  const letters = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  if (letters) return letters;
  const email = info?.email || "";
  return email ? email.charAt(0).toUpperCase() : "U";
}

export function isSectionActive(section, pathname) {
  if (section.path) return pathname === section.path;
  return (section.items || []).some((i) => pathname === i.path || pathname.startsWith(i.path + "/"));
}
